import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, Section, Btn, ArrowLink, SectionHead, Stat } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { CASES, CASE_ORDER, BRAND_DISCLAIMER } from "@/lib/content";
import NotFound from "@/pages/NotFound";

const DISCLOSURE = "This engagement was carried out as an employee of the business described, not as Prox client work. Prox is a new practice, and client case studies will be added as engagements complete.";

export default function CaseStudy() {
  const { slug } = useParams();
  const c = CASES[slug];
  if (!c) return <NotFound />;
  const idx = CASE_ORDER.indexOf(slug);
  const next = CASE_ORDER[(idx + 1) % CASE_ORDER.length];

  return (
    <>
      <Seo title={c.title} canonical={`/case-studies/${slug}`} description={c.situation.slice(0, 155)} type="article" />
      <PageHero
        eyebrow={c.label}
        crumbs={[{ label: "Home", to: "/" }, { label: "Case studies", to: "/case-studies" }, { label: c.label }]}
        title={c.title}
      />

      {/* Fact bar */}
      <Section bg="charcoal" dark pad={false} className="py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
          {Object.entries(c.bar).map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[12px] uppercase tracking-wide text-[var(--muted-dark)]">{k}</p>
              <p className="t-body text-[var(--bone)] mt-1">{v}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="bone">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead eyebrow="The situation" title="Where it started." /></div>
          <div className="lg:col-span-8 prose max-w-none"><p>{c.situation}</p></div>
        </div>
      </Section>

      <Section bg="sand">
        <SectionHead eyebrow="What was done" title="The work, in order." />
        <ul className="grid md:grid-cols-2 gap-x-10 gap-y-4 mt-10">
          {c.did.map((d, i) => (
            <Reveal i={i % 2} key={d}>
              <li className="flex gap-3 t-body"><span className="mt-[10px] w-1.5 h-1.5 bg-[var(--clay)] shrink-0" aria-hidden /><span>{d}</span></li>
            </Reveal>
          ))}
        </ul>
      </Section>

      <Section bg="charcoalDeep" dark>
        <Eyebrow>The result</Eyebrow>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-12 mt-4">
          {c.stats.map(([n, l]) => (
            <div key={l}>
              <div className="t-stat text-[var(--bone)]" style={{ fontSize: "clamp(30px,3vw,44px)" }}>{n}</div>
              <p className="t-sm text-[var(--muted-dark)] mt-3">{l}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="paper">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><SectionHead eyebrow="What transfers" title="Why this matters to you." /></div>
          <div className="lg:col-span-8 prose max-w-none">
            <p>{c.transfer}</p>
            {c.brands && <p className="t-sm text-[var(--muted)]">{BRAND_DISCLAIMER}</p>}
            <p className="t-sm text-[var(--muted)]">{DISCLOSURE}</p>
          </div>
        </div>
      </Section>

      <Section bg="bone">
        <div className="grid md:grid-cols-2 gap-5">
          <Link to={`/services/${c.related[0]}`} className="card p-8" data-testid="case-related">
            <span className="eyebrow">Related work</span>
            <h3 className="t-h4 mt-3">{c.related[1]}</h3>
            <span className="lnk mt-5 inline-flex">See the service<span className="arrow-anim">→</span></span>
          </Link>
          <Link to={`/case-studies/${next}`} className="card p-8" data-testid="case-next">
            <span className="eyebrow">Next case</span>
            <h3 className="t-h4 mt-3">{CASES[next].title}</h3>
            <span className="lnk mt-5 inline-flex">Read it<span className="arrow-anim">→</span></span>
          </Link>
        </div>
      </Section>

      <Section bg="charcoal" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">Start with the free fit call.</h2>
          <Btn to="/book" variant="clay" className="mt-9">Book the fit call</Btn>
        </div>
      </Section>
    </>
  );
}
